// File: src/pages/PostMatchPage.jsx

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TfiAlarmClock } from "react-icons/tfi";
import { useMatchFlow } from './useMatchFlow';
import EventProgressNavBar from './EventProgressNavBar';
import { useToaster } from '../../context/ToasterContext';

function getProfilePicUrl(userObj) {
  return userObj?.profilePicUrl || '/default-avatar.png';
}

export default function PostMatchPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { addToast } = useToaster();
  const [endToastShown, setEndToastShown] = useState(false);
  const endToastRef = useRef(false);

  const { state } = location;
  const { match: contextMatch } = useMatchFlow();
  const match = state?.match || contextMatch || {};

  const eId = match?.event?._id || localStorage.getItem('mobileEventId');

  /* ───────────────── toast for who ended ───────────────── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const endedFlag = params.get('ended');
    if (endedFlag && !endToastShown && !endToastRef.current) {
      if (endedFlag === 'me') addToast('You ended the match early.', 'error');
      else if (endedFlag === 'partner') addToast('Your partner ended the match early.', 'error');
      setEndToastShown(true);
      endToastRef.current = true;

      params.delete('ended');
      window.history.replaceState({}, '', location.pathname + (params.toString() ? `?${params.toString()}` : ''));
    }
  }, [location, addToast, endToastShown]);

  /* ─────────────── derive duration & participants ─────────────── */
  const durationStr = useMemo(() => {
    if (!match?.startTime || !match?.endTime) return '—';
    const startMs = new Date(match.startTime).getTime();
    const endMs = new Date(match.endTime).getTime();
    const diffMs = Math.max(0, endMs - startMs);
    const totalSec = Math.floor(diffMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }, [match?.startTime, match?.endTime]);

  const participants = Array.isArray(match?.participants) ? match.participants : [];
  const profiles = participants.map((p) => {
    const firstName = p?.firstName || 'User';
    const lastInitial = p?.lastName ? ` ${p.lastName.charAt(0).toUpperCase()}.` : '';
    return {
      _id: p?._id || String(Math.random()),
      name: `${firstName}${lastInitial}`.trim(),
      pic: getProfilePicUrl(p),
    };
  });

  function handleBackToEvent() {
    navigate(eId ? `/matchflow?eventId=${eId}` : '/matchflow');
  }

  return (
    <div style={ST.page}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Top Nav (consistent across MatchFlow pages) */}
      <EventProgressNavBar
        title="MatchFlow"
        leftLabel="Back"
        onLeftClick={handleBackToEvent}
        currentStep={5}
        totalSteps={5}
      />

      {/* Header */}
      <div style={ST.header.container}>
        <h1 style={ST.header.title}>
          Session{' '}
          <span style={ST.header.highlight}>
            <span style={ST.header.highlightText}>Ended</span>
          </span>
        </h1>
        <div style={{ ...ST.header.subheading, animation: 'fadeUp .24s ease-out' }}>
          We hope you had a great conversation.
        </div>
      </div>

      {/* Summary Card */}
      <div style={ST.card}>
        <div style={ST.cardRow}>
          <div style={ST.iconCircle}>
            <TfiAlarmClock style={{ fontSize: 18, color: '#0b0b0b' }} />
          </div>
          <div style={ST.meta}>
            <div style={ST.metaLabel}>Duration</div>
            <div style={ST.metaValue}>{durationStr}</div>
          </div>
        </div>

        <div style={ST.divider} />

        <div style={ST.cardRowColumn}>
          <div style={ST.metaLabel}>Participants</div>

          {/* Avatar stack (up to 3 shown), then names row */}
          <div style={ST.avatarStackWrap}>
            <div style={ST.avatarStack}>
              {profiles.slice(0, 3).map((p, i) => (
                <img
                  key={p._id}
                  src={p.pic}
                  alt=""
                  style={{
                    ...ST.avatar,
                    left: i * 20,
                    zIndex: 10 - i,
                    borderColor: '#fff',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={ST.namesRow}>
            {profiles.map((p) => (
              <span key={p._id} style={ST.namePill}>
                <img src={p.pic} alt="" style={ST.nameAvatar} />
                {p.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div style={ST.actionBar}>
        <button style={ST.actionButton} onClick={handleBackToEvent}>
          Back to Event
        </button>
      </div>
    </div>
  );
}

/* =================== Styles (MatchFlow tokens) =================== */
const ST = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },

  header: {
    container: {
      boxSizing: 'border-box',
      maxWidth: 680,
      width: '80%',
      margin: '12px auto 8px',
      padding: '0 12px',
      textAlign: 'center',
      color: '#0b0b0b',
    },
    title: {
      margin: 0,
      fontSize: '1.85rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    highlight: {
      display: 'inline-block',
      backgroundColor: '#3579F6',
      color: '#fff',
      padding: '3px 10px',
      paddingBottom: 4,
      borderRadius: 6,
      transform: 'skewX(-6deg) rotate(-2deg)',
      marginLeft: 6,
    },
    highlightText: {
      display: 'inline-block',
      transform: 'skewX(6deg) rotate(2deg)',
    },
    subheading: {
      marginTop: 6,
      fontSize: '.9rem',
      color: '#6b7280',
      fontWeight: 500,
      lineHeight: 1.45,
    },
  },

  card: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: 720,
    margin: '12px auto 120px',
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid #eef0f2',
    background: '#fff',
    boxShadow: '0 10px 24px rgba(15,23,42,0.05)',
  },

  cardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '2px 2px',
  },

  cardRowColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '4px 2px',
  },

  iconCircle: {
    width: 50,
    height: 50,
    borderRadius: 999,
    background: '#f8fafc',
    border: '1px solid #eef2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },

  meta: {
    display: 'flex',
    flexDirection: 'column',
  },
  metaLabel: {
    fontSize: '.78rem',
    fontWeight: 800,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: '#6b7280',
  },
  metaValue: {
    fontSize: '1.05rem',
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-0.015em',
    marginTop: 2,
  },

  divider: {
    height: 1,
    background: '#eef0f2',
    margin: '12px 0',
  },

  avatarStackWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '6px 0 2px',
  },
  avatarStack: {
    position: 'relative',
    width: 100,
    height: 40,
  },
  avatar: {
    position: 'absolute',
    top: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #fff',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  },

  namesRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  namePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px 3px 2px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0f172a',
    fontSize: '.85rem',
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  nameAvatar: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    border: '1px solid #fff',
  },

  actionBar: {
    position: 'fixed',
    inset: 'auto 0 0 0',
    background: '#fff',
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    maxWidth: 760,
    margin: '0 auto',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    zIndex: 50,
  },
  actionButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};
